import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { AppTheme, Category, OrderData, Product, SupplyType } from '../interfaces';
import { createPathFromCategory, fetchData, matchPathToCategories } from '../helpers';
import { BRANCH_ROUTE, CATEGORY_ROUTE, PRODUCT_ROUTE } from '../constants/routes';

interface ProviderProps {
    children: React.ReactElement;
    categoryTree: Category[];
    products: Product[];
    supplyTypes: SupplyType[];
    formUri: string;
    privacyUri: string;
    termsAndConditionsUri: string;
    appNodeIdentifier: string;
    appNodeDimensions: object;
    productsSelection: Product[];
    productSelection: string;
    contactsWrapper: Element;
    gtagEventTarget: string;
    allowOrderAsAlways: boolean;
    selectionLabel: string;
    isShop: boolean;
    inquiriesOnly: boolean;
    orderModeSelectable: boolean;
    allowUploads: boolean;
    locale: string;
    maxFileSize: number;
    theme: AppTheme;
}

interface ProviderValues {
    categoryTree: Category[];
    contactsWrapper: Element;
    gtagEventTarget: string;
    isSubmitting: boolean;
    parentCategories: Category[];
    privacyUri: string;
    productSelection: Product;
    products: Product[];
    productsSelection: Product[];
    selectCategory: (category: Category) => void;
    selectProduct: (product?: Product, section?: string) => void;
    selectedCategory: Category;
    selectedProduct: Product;
    submitOrder: (formData: OrderData) => Promise<any>;
    supplyTypes: SupplyType[];
    termsAndConditionsUri: string;
    viewProductsForBranch: (category: Category) => void;
    allowOrderAsAlways: boolean;
    selectionLabel: string;
    /**
     * Switch, if the app is rendered as JobFinder (isShop = false) or as ProductFinder (isShop = true)
     */
    isShop: boolean;
    /**
     * Switch, if the ProductFinder renders the variant for "Bestellung" (inquiriesOnly = false) or
     * "Bestellanfrage" (inquiriesOnly = false)
     */
    inquiriesOnly: boolean;
    /**
     * Switch, if the ProductFinder renders a button for the user to choose "Bestellung" or "Bestellanfrage".
     */
    orderModeSelectable: boolean;
    allowUploads: boolean;
    locale: string;
    maxFileSize: number;
    theme: AppTheme;
}

interface AppRouteMatch {
    isExact: boolean;
    params: {
        categories?: string;
        productSlug?: string;
    };
}

export const AppDataContext = createContext(null);
export const useAppData = (): ProviderValues => useContext(AppDataContext);

export const AppDataProvider: React.FC<ProviderProps> = ({
    children,
    categoryTree,
    products,
    supplyTypes,
    formUri,
    privacyUri,
    termsAndConditionsUri,
    appNodeIdentifier,
    appNodeDimensions,
    productSelection,
    productsSelection,
    contactsWrapper,
    gtagEventTarget,
    allowOrderAsAlways,
    isShop,
    inquiriesOnly,
    orderModeSelectable,
    selectionLabel,
    allowUploads,
    maxFileSize,
    theme,
    locale = 'de',
}: ProviderProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product>();
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [parentCategories, setParentCategories] = useState<Category[]>([]);
    const history = useHistory();

    const rootMatch: AppRouteMatch = useRouteMatch('/');
    const categoryMatch: AppRouteMatch = useRouteMatch(CATEGORY_ROUTE);
    const productMatch: AppRouteMatch = useRouteMatch(PRODUCT_ROUTE);
    const branchMatch: AppRouteMatch = useRouteMatch(BRANCH_ROUTE);

    const selectCategory = (category?: Category) => {
        if (!category) {
            history.push('/');
        } else if (category !== selectedCategory) {
            history.push(`/category/${createPathFromCategory(category)}`);
        } else if (category.parentPath) {
            history.push(`/category/${category.parentPath}`);
        } else {
            history.push('/');
        }
    };

    const viewProductsForBranch = (category?: Category) => {
        if (category) {
            history.push(`/branch/${createPathFromCategory(category)}`);
        } else {
            history.push('/');
        }
    };

    const selectProduct = (product?: Product, section = '') => {
        if (product) {
            if (selectedCategory) {
                history.push(
                    `/product/${createPathFromCategory(selectedCategory)}/${product.slug}` +
                        (section ? '#' + section : '')
                );
            } else {
                history.push(`/product/${product.slug}` + (section ? '#' + section : ''));
            }
        } else {
            history.push('/');
        }
    };

    const submitOrder = (formData: OrderData) => {
        setIsSubmitting(true);
        return new Promise((resolve, reject) => {
            return fetchData(
                formUri,
                {
                    ...formData,
                    appNodeIdentifier,
                    appNodeDimensions: JSON.stringify(appNodeDimensions),
                },
                'POST',
                true
            )
                .then((result) => {
                    setIsSubmitting(false);
                    resolve(result);
                })
                .catch((error) => {
                    setIsSubmitting(false);
                    reject(error);
                });
        });
    };

    // Initial product selection if set
    useEffect(() => {
        if (productSelection) {
            selectProduct(products.find((product) => product.id === productSelection));
        }
    }, []);

    // Converts the current relative app uri in a list of selected categories
    useEffect(() => {
        const categoryPath =
            categoryMatch?.params?.categories || productMatch?.params?.categories || branchMatch?.params?.categories;

        let categories = [];
        if (categoryPath) {
            try {
                categories = matchPathToCategories(categoryPath, categoryTree);
            } catch (e) {
                console.error(e);
                history.push('/');
            }
        }

        if (categories.length > 0) {
            setSelectedCategory(categories[categories.length - 1]);
            setParentCategories(categories);
        } else {
            setSelectedCategory(null);
            setParentCategories([]);
            setSelectedProduct(null);
        }
    }, [categoryMatch?.params?.categories, productMatch?.params?.categories]);

    // Converts the current relative app uri in a product selection
    useEffect(() => {
        const productSlug = productMatch?.params?.productSlug;

        if (productSlug) {
            setSelectedProduct(products.find((product) => product.slug === productSlug));
        } else {
            setSelectedProduct(null);
        }
    }, [productMatch?.params?.productSlug]);

    // Unset product selection on base url
    useEffect(() => {
        if (rootMatch?.isExact) {
            setSelectedProduct(null);
        }
    }, [rootMatch]);

    return (
        <AppDataContext.Provider
            value={{
                categoryTree,
                contactsWrapper,
                gtagEventTarget,
                isSubmitting,
                parentCategories,
                privacyUri,
                productSelection,
                products,
                productsSelection,
                selectionLabel,
                selectCategory,
                selectProduct,
                selectedCategory,
                selectedProduct,
                submitOrder,
                supplyTypes,
                termsAndConditionsUri,
                viewProductsForBranch,
                allowOrderAsAlways,
                isShop,
                inquiriesOnly,
                orderModeSelectable,
                allowUploads,
                maxFileSize,
                locale,
                theme,
            }}
        >
            {children}
        </AppDataContext.Provider>
    );
};
